// Copyright 2025, University of Maryland, All Rights Reserved
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Trash2, Plus, Copy, ChevronUp, ChevronDown } from "lucide-react"
import { useState } from "react"
import type { SchemaBuilderSection } from "@/types/schema-builder"
import type { SchemaField, SchemaFieldType } from "@/types/schema-fields"
import { getFieldEditorComponent } from "./field-utils"
import { getIconComponent, getCommonIconNames } from "@/utils/icon-utils"
import { getAvailableFieldTypesForSchema, FIELD_TYPE_CONFIGS } from "./field-type-definitions"

interface SectionEditorProps {
  section: SchemaBuilderSection
  schema_type: string
  onSectionChange: (sectionUuid: string, updates: Partial<SchemaBuilderSection>) => void
  onDuplicate: (sectionId: string) => void
  onRemove: (sectionId: string) => void
  onAddField: (sectionUuid: string, field_type: SchemaFieldType) => void
  onRemoveField: (sectionUuid: string, fieldUuid: string) => void
  onUpdateField: (sectionUuid: string, fieldUuid: string, updates: Partial<SchemaField>) => void
  onMoveFieldUp: (sectionUuid: string, fieldUuid: string) => void
  onMoveFieldDown: (sectionUuid: string, fieldUuid: string) => void
  onMoveSectionUp: (sectionUuid: string) => void
  onMoveSectionDown: (sectionUuid: string) => void
  isLoading?: boolean
  // Position info for section arrows
  isFirstSection?: boolean
  isLastSection?: boolean
}


export const SectionEditor = ({ 
  section, 
  schema_type,
  onSectionChange,
  onDuplicate,
  onRemove,
  onAddField,
  onRemoveField,
  onUpdateField,
  onMoveFieldUp,
  onMoveFieldDown,
  onMoveSectionUp,
  onMoveSectionDown,
  isLoading,
  isFirstSection = false,
  isLastSection = false
}: SectionEditorProps) => {
  const IconComponent = getIconComponent(section.icon)
  
  // Check if this is a required summary section for monitor schema
  const isRequiredSummarySection = section.id === 'summary' && schema_type === 'monitor'
  
  // Initialize selected field type with a stable default
  const [selected_field_type, setSelectedFieldType] = useState<SchemaFieldType>('string')
  
  // Get available field types for this schema
  const available_types = getAvailableFieldTypesForSchema()
  
  // Event handlers for field operations - delegate to parent
  const handleFieldUpdate = (field: SchemaField, updates: Partial<SchemaField>) => {
    onUpdateField(section.uuid, field.uuid, updates)
  }

  const handleFieldRemove = (field: SchemaField) => {
    onRemoveField(section.uuid, field.uuid)
  }

  const handleFieldMoveUp = (field: SchemaField) => {
    onMoveFieldUp(section.uuid, field.uuid)
  }

  const handleFieldMoveDown = (field: SchemaField) => {
    onMoveFieldDown(section.uuid, field.uuid)
  }
  
  return (
    <div className="border rounded-lg p-4 space-y-4 bg-white">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {IconComponent && <IconComponent className="h-4 w-4" />}
          <span className="font-medium">{section.title || 'Untitled Section'}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Section Reordering Arrows */}
          <div className="flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveSectionUp(section.uuid)}
              disabled={isLoading || isFirstSection}
              title="Move section up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveSectionDown(section.uuid)}
              disabled={isLoading || isLastSection}
              title="Move section down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDuplicate(section.id)}
            disabled={isLoading}
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </Button>
          {!isRequiredSummarySection && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(section.id)}
              disabled={isLoading}
              title="Delete"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>

      {/* Required Section Indicator */}
      {isRequiredSummarySection && (
        <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded-md border border-blue-200">
          <strong>Required Section:</strong> This summary section is required for monitor schemas and contains the filter score field.
        </div>
      )}

      {/* Section Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Section ID</Label>
          <Input
            value={section.id}
            onChange={(e) => onSectionChange(section.uuid, { id: e.target.value })}
            placeholder="section_id"
            className="font-mono text-sm"
            disabled={isLoading || isRequiredSummarySection}
          />
          {isRequiredSummarySection && (
            <p className="text-xs text-muted-foreground">
              Summary section ID is required and cannot be changed
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            value={section.title}
            onChange={(e) => onSectionChange(section.uuid, { title: e.target.value })}
            placeholder="Section Title"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Icon Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Icon</Label>
          <Select
            value={section.icon || '__none__'}
            onValueChange={(value) => onSectionChange(section.uuid, { icon: value === '__none__' ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select icon">
                {section.icon && (
                  <div className="flex items-center gap-2">
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    {section.icon}
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No Icon</SelectItem>
              {getCommonIconNames().map((icon_name) => {
                const Icon = getIconComponent(icon_name)
                return (
                  <SelectItem key={icon_name} value={icon_name}>
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="h-4 w-4" />}
                      {icon_name}
                    </div>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* Fields */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm">Fields</h4>
          <div className="flex items-center gap-2">
              <div className="w-48">
                <div className="space-y-2">
                  <Label className="text-sm">Field Type</Label>
                  <Select 
                    value={selected_field_type} 
                    onValueChange={(value: string) => setSelectedFieldType(value as SchemaFieldType)}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field type" />
                    </SelectTrigger>
                    <SelectContent>
                      {available_types.map((type) => {
                        const config = FIELD_TYPE_CONFIGS[type]
                        return (
                          <SelectItem key={type} value={type}>
                            <div>
                              <div className="font-medium">{config.label}</div>
                              <div className="text-sm text-muted-foreground">{config.description}</div>
                            </div>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAddField(section.uuid, selected_field_type)}
              disabled={isLoading}
              title={"Add field to this section"}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </div>
        </div>
        
        {section.fields.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded-lg">
            <p className="text-sm">No fields configured</p>
            <p className="text-xs">Add fields to define what the AI should analyze</p>
          </div>
        ) : (
          <div className="space-y-3">
            {section.fields.map((field, index) => {
              const FieldEditor = getFieldEditorComponent(field.field_type)
              
              // Check if this is the filter_score field in summary section of monitor schema
              const is_filter_score_field = field.field_type === 'score' && 
                                           field.id === 'filter_score' && 
                                           section.id === 'summary' && 
                                           schema_type === 'monitor'
              
              return (
                <FieldEditor
                  key={field.uuid}
                  field={field}
                  onFieldUpdate={handleFieldUpdate}
                  onRemove={handleFieldRemove}
                  onMoveUp={handleFieldMoveUp}
                  onMoveDown={handleFieldMoveDown}
                  isLoading={isLoading}
                  isFirst={index === 0}
                  isLast={index === section.fields.length - 1}
                  readonly_id={is_filter_score_field}
                  non_deletable={is_filter_score_field}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
